import { useLDClient } from 'launchdarkly-react-client-sdk';
import { useContext, useEffect, useState } from 'react';
import { FeatureFlagOverrideContext } from '@/lib/providers/featureFlagOverrideContext';
import type { FlagKeys } from '@/lib/providers/featureFlagProvider';
import { isDevToolsEnabled } from '@/lib/utils/dev-tools/isDevToolsEnabled';

/**
 * Custom hook for accessing a single feature flag.
 * More efficient than useFlags() when you only need one flag,
 * as it only re-renders when that specific flag changes.
 *
 * Supports dev tools overrides in development/staging environments.
 *
 * Usage:
 *   const isEnabled = useFlag('myFeatureFlag');
 *
 * @param flagKey - The key of the flag to retrieve
 * @returns The current value of the flag
 */
export function useFlag<K extends keyof FlagKeys>(flagKey: K): FlagKeys[K] {
  const ldClient = useLDClient();
  const context = useContext(FeatureFlagOverrideContext);

  // Get initial value from LaunchDarkly with proper default
  const getDefaultValue = (): FlagKeys[K] => {
    // Try to get from override first
    if (isDevToolsEnabled() && context?.overrides?.[flagKey] !== undefined) {
      return context.overrides[flagKey] as FlagKeys[K];
    }
    // Then try LaunchDarkly
    if (ldClient) {
      return ldClient.variation(flagKey as string, false as FlagKeys[K]);
    }
    // Fallback to false for boolean flags
    return false as FlagKeys[K];
  };

  const [flagValue, setFlagValue] = useState<FlagKeys[K]>(getDefaultValue);

  // Check for dev tools override
  const hasOverride = isDevToolsEnabled() && context?.overrides?.[flagKey] !== undefined;
  const overrideValue = context?.overrides?.[flagKey];

  // Subscribe to changes for this specific flag
  useEffect(() => {
    if (!ldClient) {
      return;
    }

    // Update state with current value
    const currentValue = ldClient.variation(flagKey as string, false as FlagKeys[K]);
    setFlagValue(currentValue);

    // Listen for changes to this specific flag
    const changeHandler = (changes: Record<string, { current: unknown; previous: unknown }>) => {
      const change = changes[flagKey];
      if (change) {
        const newValue = change.current as FlagKeys[K];
        setFlagValue(newValue);
      }
    };

    ldClient.on('change', changeHandler);

    return () => {
      ldClient.off('change', changeHandler);
    };
  }, [ldClient, flagKey]);

  // Return override value if present, otherwise return flag value
  return hasOverride ? (overrideValue as FlagKeys[K]) : flagValue;
}
